import type { DeclaredType } from '../parser/ast.js';
import type { RuntimeValue } from './runtime-value.js';

/**
 * Αποθήκη μεταβλητών. Ένα επίπεδο (flat) για όλο το πρόγραμμα — δεν
 * υπάρχουν ακόμα διαδικασίες/συναρτήσεις που θα χρειάζονταν τοπικό scope.
 * Αγνοεί εντελώς AST/σφάλματα· ο Interpreter αποφασίζει τι σημαίνει μια
 * απών τιμή (π.χ. "μη αρχικοποιημένη μεταβλητή").
 */
export class Environment {
  private readonly values = new Map<string, RuntimeValue>();

  /**
   * Δηλωμένος τύπος ανά όνομα — μόνο για μεταβλητές/πίνακες ΓΛΩΣΣΑΣ που
   * δηλώθηκαν ρητά στο ΜΕΤΑΒΛΗΤΕΣ (η Ψευδογλώσσα δεν έχει τυπικές δηλώσεις,
   * άρα παραμένει εντελώς δυναμική). Χρησιμοποιείται από τον Interpreter για
   * να απορρίπτει αναθέσεις/ΔΙΑΒΑΣΕ λάθος τύπου.
   */
  private readonly declaredTypes = new Map<string, DeclaredType>();

  /** Ονόματα δηλωμένα στο ΣΤΑΘΕΡΕΣ (μόνο ΓΛΩΣΣΑ) — δεν επιτρέπεται νέα ανάθεση μετά την αρχική τιμή τους (βλ. `Interpreter.requireNotConstant`). */
  private readonly constantNames = new Set<string>();

  define(name: string, value: RuntimeValue): void {
    this.values.set(name, value);
  }

  has(name: string): boolean {
    return this.values.has(name);
  }

  get(name: string): RuntimeValue | undefined {
    return this.values.get(name);
  }

  declareType(name: string, type: DeclaredType): void {
    this.declaredTypes.set(name, type);
  }

  getDeclaredType(name: string): DeclaredType | undefined {
    return this.declaredTypes.get(name);
  }

  markConstant(name: string): void {
    this.constantNames.add(name);
  }

  isConstant(name: string): boolean {
    return this.constantNames.has(name);
  }

  /** Στιγμιότυπο όλων των μεταβλητών — για εμφάνιση (π.χ. panel μεταβλητών σε web UI), όχι για μεταβολή. */
  snapshot(): ReadonlyMap<string, RuntimeValue> {
    return this.values;
  }
}
